'use client';

import { usePathname } from 'next/navigation';
import { FC, PropsWithChildren, createContext, useContext, useState } from 'react';
import BlockCustomerForm from '~/data/strapi/types/shared-blocks/block-customer-form';

import CustomerFormModal from '../form-modal';

const DEFAULT_MODAL_ANCHOR = 'modalForm';

interface OpenModalOptions {
  withoutUrlChange?: boolean;
  serviceName?: string;
}

interface CallModalContextValue {
  openModal: (options?: OpenModalOptions) => void;
  modalAnchor: string;
  isAvailable: boolean;
}

const CallModalContext = createContext<CallModalContextValue>({
  openModal: () => {},
  modalAnchor: DEFAULT_MODAL_ANCHOR,
  isAvailable: false,
});

export const useCallModal = () => useContext(CallModalContext);

interface Props {
  customerFormData: BlockCustomerForm;
}

const CallModalProvider: FC<PropsWithChildren<Props>> = ({ customerFormData, children }) => {
  const pathname = usePathname();
  const [visible, setVisible] = useState<boolean>(false);
  const [serviceName, setServiceName] = useState<string | undefined>();

  const modalAnchor = customerFormData.modal?.anchor || DEFAULT_MODAL_ANCHOR;

  const openModal = (options?: OpenModalOptions) => {
    setServiceName(options?.serviceName);
    setVisible(true);
    if (!options?.withoutUrlChange) {
      window.history.replaceState({}, '', `${pathname}${window.location.search}#${modalAnchor}`);
    }
  };

  const closeModal = () => {
    window.history.replaceState({}, '', `${pathname}${window.location.search}`);
    setVisible(false);
    setServiceName(undefined);
  };

  return (
    <CallModalContext.Provider value={{ openModal, modalAnchor, isAvailable: true }}>
      {children}
      <CustomerFormModal
        customerFormData={customerFormData}
        visible={visible}
        onClose={closeModal}
        serviceName={serviceName}
      />
    </CallModalContext.Provider>
  );
};

export default CallModalProvider;
